// Выбор шаблона поездки. Один и тот же список работает в двух местах: на
// странице поездок он заводит новую поездку, на пустой доске — собирает вариант
// в уже открытой.
//
// Свёрнутый <details> выбран нарочно: шаблон — предложение, а не следующий шаг
// работы. Тому, кто пришёл к своему списку поездок, он не мешает и не занимает
// первый экран; тому, у кого ещё ничего нет, открывается сразу — там подсказать
// уместно.
import { useState, type ReactElement } from 'react';
import { useT } from '../i18n';
import { KindIcon } from '../ui/library/KindIcon';
import { TRIP_TEMPLATES, blockCountLabel, blockLabel, type TripTemplate } from './catalog';
import styles from './TripTemplates.module.css';

export interface TripTemplatesProps {
  /** Ключ подписи действия: «Создать поездку» либо «Собрать вариант». */
  actionKey: string;
  /** Ключ строки о том, что именно случится по нажатию. */
  noteKey: string;
  /** Раскрыть список сразу — когда собирать больше нечего и подсказка кстати. */
  defaultOpen?: boolean;
  busy?: boolean;
  onPick(tpl: TripTemplate): void;
}

export function TripTemplates({
  actionKey,
  noteKey,
  defaultOpen = false,
  busy = false,
  onPick,
}: TripTemplatesProps): ReactElement {
  const t = useT();
  // Как и у частых блоков: раскрытость снимается один раз, чтобы появившийся
  // блок или поездка не захлопнули раздел в момент работы с ним.
  const [open] = useState(defaultOpen);

  return (
    <details className={styles.wrap} open={open}>
      <summary className={styles.summary}>
        <span className={styles.summaryTitle}>{t('tpl.start')}</span>
        <span className={styles.summaryHint}>{t('tpl.start.hint')}</span>
      </summary>

      <p className={styles.note}>{t(noteKey)}</p>

      <ul className={styles.grid}>
        {TRIP_TEMPLATES.map((tpl) => (
          <li key={tpl.id} className={styles.cell}>
            {/* Карточка целиком — одна кнопка: попасть в неё проще, чем в
                подпись где-то в углу, а других действий у шаблона нет. */}
            <button
              type="button"
              className={styles.card}
              disabled={busy}
              onClick={() => onPick(tpl)}
            >
              <span className={styles.name}>{t(tpl.nameKey)}</span>
              <span className={styles.desc}>{t(tpl.descKey)}</span>
              <span className={styles.chips}>
                {tpl.blocks.map((spec, i) => {
                  const count = blockCountLabel(spec, t);
                  return (
                    <span key={`${spec.id}-${i}`} className={styles.chip}>
                      <KindIcon kind={spec.kind} size={12} />
                      {blockLabel(spec, t)}
                      {count !== '' && <span className={styles.chipCount}>{count}</span>}
                    </span>
                  );
                })}
              </span>
              <span className={styles.action}>{t(busy ? 'auth.busy' : actionKey)}</span>
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
